import { Injectable } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import ms from 'ms';
import { retry, timeout, map, takeUntil } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { ApiBaseService } from './api-base.service';
import { IError } from 'projects/common/src/app/interfaces';
import { ICrud } from 'projects/common/src/app/interfaces/communication';
import { IRequestConfig } from 'projects/common/src/app/interfaces/communication/request';
import { IRequestResponse } from 'projects/common/src/app/interfaces/communication/response';
import { ServerConfigService } from 'src/app/services/server-config.service';

@Injectable({
  providedIn: 'root'
})
export class CrudService extends ApiBaseService implements ICrud {

  constructor(
    protected http: HttpClient,
    protected configServer: ServerConfigService
  ) {
    super(configServer);
  }

  public get(
    endPoint: string,
    parameters?: {[key: string]: string},
    config: IRequestConfig = {},
    apply?: (arg0: any) => any,
  ): Observable<any> {
    if (!endPoint) {
      throw {
        message: 'no_endpoint_url_provided',
      } as IError;
    }
    const pathUrl = this.buildUrl(endPoint);
    const requestTimeout = config.timeout || ms(this.config.timeout);
    const requestRetry = config.retry || this.config.retry;
    return this.http.get(pathUrl, {params: parameters}).pipe(
      timeout(requestTimeout),
      retry(requestRetry),
      map((response: IRequestResponse) => this.isSuccessResponse(response) ? response.data : response.error),
      map(x => apply ? apply(x) : x)
    );
  }

  public post(
    endPoint: string,
    body: {[key: string]: string},
    parameters?: {[key: string]: string},
    config: IRequestConfig = {},
    apply?: (arg0: any) => any,
    cancellableSubject?: Subject<any>
  ): Observable<any> {
    if (!endPoint) {
      throw {
        message: 'no_endpoint_url_provided',
      } as IError;
    }
    const pathUrl = this.buildUrl(endPoint);
    const requestTimeout = config.timeout || ms(this.config.timeout);
    const requestRetry = config.retry || this.config.retry;
    return this.http.post(pathUrl, body, {params: parameters}).pipe(
      timeout(requestTimeout),
      retry(requestRetry),
      map((response: IRequestResponse) => this.isSuccessResponse(response) ? response.data : response),
      map(x => apply ? apply(x) : x),
      takeUntil(cancellableSubject || new Subject())
    );
  }

  public patch(
    endPoint: string,
    body: any,
    config?: IRequestConfig
  ): Observable<any> {
    const pathUrl = this.buildUrl(endPoint);
    const requestTimeout = config ? config.timeout : ms(this.config.timeout);
    const requestRetry = config ? config.retry : this.config.retry;
    const requestResult = this.http.post(pathUrl, body).pipe(
      timeout(requestTimeout),
      retry(requestRetry),
      map((response: IRequestResponse) => this.isSuccessResponse(response) ? response.data : response.error),
    );

    return requestResult;
  }

  public delete(
    endPoint: string,
    config?: IRequestConfig
  ): Observable<any> {
    const pathUrl = this.buildUrl(endPoint);
    const requestTimeout = config ? config.timeout : ms(this.config.timeout);
    const requestRetry = config ? config.retry : this.config.retry;
    const requestResult = this.http.delete(pathUrl).pipe(
      timeout(requestTimeout),
      retry(requestRetry),
      map((response: IRequestResponse) => this.isSuccessResponse(response) ? response.data : response.error),
    );
    return requestResult;
  }

  /**
   * Returns ``true`` if response does not have error values in its parameters
   * @param response http response object
   */
  protected isSuccessResponse(response: IRequestResponse): boolean {
    return response && response.error && !response.error.errorNumber;
  }
}
